import type { CasesTimeByStep } from '@compass/client/types/cases_analytics';
import React, { useMemo, useState } from 'react';

import { ChartCard } from '@/pages/analytics/components/ChartCard';
import { ChartRenderBoundary } from '@/pages/analytics/components/ChartRenderBoundary';
import { ChartBodySkeleton, ChartEmptyState } from '@/pages/analytics/components/ChartStates';
import { DotsLegend } from '@/pages/analytics/components/DotsLegend';
import { ScopeToggle } from '@/pages/analytics/components/ScopeToggle';
import { StackedBarChart } from '@/pages/analytics/components/StackedBarChart';
import type { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import type { TrendBucket } from '@/pages/analytics/types';
import { formatChartAxisDate, formatChartTooltipDate } from '@/pages/analytics/utils/chartDate';
import { CHART_COLOR_VARS } from '@/pages/analytics/utils/chartPalette';
import { formatBucketLabel } from '@/pages/analytics/utils/dateWindow';
import { formatInt } from '@/pages/analytics/utils/formatters';
import { normalizeStepTime } from '@/pages/analytics/utils/normalizeChartRows';

import { formatDurationHm, formatSignedDurationHm } from '../utils/formatDuration';
import {
  SEGMENT_LABELS,
  type SegmentRow,
  STEP_ORDER,
  type StepKey,
  stepSeriesLabel,
  summarizeSteps,
} from '../utils/steps';

type Tab = 'summary' | 'trend';

const TAB_OPTIONS: { value: Tab; label: string }[] = [
  { value: 'summary', label: 'Summary' },
  { value: 'trend', label: 'Over time' },
];

const TITLE = 'Time Spent at Each Step';
const HEIGHT = 280;
const SECONDS_PER_HOUR = 3600;

/** One theme chart colour per step, in order, as the rest of the page colours its groups. */
const stepColor = (step: StepKey): string => CHART_COLOR_VARS[STEP_ORDER.indexOf(step)];

const legendPayload = (totals: (number | undefined)[]) =>
  STEP_ORDER.map((step, i) => ({
    value: stepSeriesLabel(step),
    color: stepColor(step),
    trailing: formatDurationHm(totals[i]),
  }));

const SEGMENT_LABEL_CLASS = {
  all: 'font-medium text-foreground',
  within_sla: 'text-muted-foreground',
  breached: 'text-critical-foreground',
} as const;

/** The step bar of one case set, scaled against the slowest set so the three compare. */
const SegmentBar: React.FC<{ row: SegmentRow; scale: number }> = ({ row, scale }) => (
  <div className="flex h-6 items-stretch gap-0.5" aria-hidden>
    {row.steps.map((seconds, i) =>
      seconds ? (
        <div
          key={STEP_ORDER[i]}
          className="rounded-[3px]"
          style={{
            width: `${(seconds / scale) * 100}%`,
            backgroundColor: stepColor(STEP_ORDER[i]),
            // The three sets share a palette; only the "All Cases" bar is at full strength.
            opacity: row.segment === 'all' ? 1 : 0.5,
          }}
          title={`${stepSeriesLabel(STEP_ORDER[i])}: ${formatDurationHm(seconds)}`}
        />
      ) : null,
    )}
  </div>
);

const SummaryBars: React.FC<{ summary: CasesTimeByStep['summary'] }> = ({ summary }) => {
  const { rows, segments } = useMemo(() => summarizeSteps(summary), [summary]);
  const scale = Math.max(...segments.map(s => s.total ?? 0), 1);
  const within = segments.find(s => s.segment === 'within_sla');
  const numeric = 'px-3 py-2 text-right align-middle tabular-nums text-foreground';
  const head = 'px-3 pb-2 text-right text-[11px] font-medium uppercase text-muted-foreground';

  // Extra time this set spends against the cases that stayed within SLA.
  const delta = (row: SegmentRow, key: 'tier1' | 'tier2' | 'total'): number | undefined => {
    if (row.segment !== 'breached') return undefined;
    const theirs = within?.[key];
    const ours = row[key];
    return theirs !== undefined && ours !== undefined ? ours - theirs : undefined;
  };

  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th scope="col" className="px-3 pb-2">
              <span className="sr-only">Cases</span>
            </th>
            <th scope="col" className="px-3 pb-2">
              <span className="sr-only">Time at each step</span>
            </th>
            <th scope="col" className={head}>
              Tier 1
            </th>
            <th scope="col" className={head}>
              Tier 2
            </th>
            <th scope="col" className={head}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {segments.map(row => (
            <tr key={row.segment} className={row.segment === 'all' ? 'bg-muted/40' : undefined}>
              <th scope="row" className="whitespace-nowrap px-3 py-2 text-left align-middle">
                <div className={SEGMENT_LABEL_CLASS[row.segment]}>
                  {SEGMENT_LABELS[row.segment]}
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  {formatInt(row.caseCount)} cases
                </div>
              </th>
              <td className="w-1/2 py-2 pr-3">
                <SegmentBar row={row} scale={scale} />
              </td>
              {(['tier1', 'tier2', 'total'] as const).map(key => (
                <td key={key} className={numeric}>
                  <div>{formatDurationHm(row[key])}</div>
                  {delta(row, key) !== undefined && (
                    <div className="text-xs text-critical-foreground">
                      {formatSignedDurationHm(delta(row, key))}
                    </div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <DotsLegend payload={legendPayload(rows.map(r => r.all))} />
    </>
  );
};

const TrendBars: React.FC<{
  trend: CasesTimeByStep['trend'];
  trendBucket: TrendBucket;
  bucketDates: string[];
  partialDates: Set<string>;
}> = ({ trend, trendBucket, bucketDates, partialDates }) => {
  // Hours, not seconds: recharts picks its ticks from the values, and hours give whole ones.
  const rows = useMemo(
    () =>
      STEP_ORDER.flatMap(step =>
        (trend ?? [])
          .filter(p => p.step === step)
          .map(p => ({
            x: p.date ?? '',
            group: step,
            group_name: stepSeriesLabel(step),
            value: (p.avg_seconds ?? 0) / SECONDS_PER_HOUR,
          })),
      ),
    [trend],
  );

  return (
    <StackedBarChart
      rows={rows}
      xValues={bucketDates}
      xFormatter={x => formatBucketLabel(x, formatChartAxisDate(x, trendBucket), partialDates)}
      tooltipXFormatter={x =>
        formatBucketLabel(x, formatChartTooltipDate(x, trendBucket), partialDates)
      }
      valueFormatter={hours => formatDurationHm(hours * SECONDS_PER_HOUR)}
      yFormatter={hours => `${hours}h`}
      colorByKey={new Map(STEP_ORDER.map(step => [step, stepColor(step)]))}
      ariaLabelPrefix="Time per step bar chart"
      seriesNoun="step"
      height={HEIGHT}
    />
  );
};

interface StepTimeCardProps {
  data?: CasesTimeByStep;
  trendBucket: TrendBucket;
  /** Chart labels of every bucket sent, so empty buckets still get a bar slot. */
  bucketDates: string[];
  /** Chart labels of buckets cut by the range (marked `*`). */
  partialDates: Set<string>;
  download: ReturnType<typeof useDownloadChart>;
  loading?: boolean;
}

export const StepTimeCard: React.FC<StepTimeCardProps> = ({
  data,
  trendBucket,
  bucketDates,
  partialDates,
  download,
  loading,
}) => {
  const [tab, setTab] = useState<Tab>('summary');
  const isEmpty = tab === 'summary' ? !data?.summary?.length : !data?.trend?.length;

  return (
    <ChartCard
      title={TITLE}
      rightSlot={<ScopeToggle value={tab} onChange={setTab} options={TAB_OPTIONS} />}
      onDownload={(format, exportElement) =>
        data && download(normalizeStepTime(data), format, TITLE, exportElement)
      }
      downloadDisabled={!data}
    >
      <p className="pb-3 text-xs text-muted-foreground">
        {tab === 'summary'
          ? 'Average per step and tier, with all cases, those within SLA and those that breached it side by side.'
          : 'Average per step for the cases the filters select, stacked into the time a case takes.'}
      </p>
      {loading ? (
        <ChartBodySkeleton height={HEIGHT} />
      ) : (
        <ChartRenderBoundary resetKeys={[data, tab, trendBucket]} height={HEIGHT}>
          {isEmpty ? (
            <ChartEmptyState height={HEIGHT} />
          ) : tab === 'summary' ? (
            <SummaryBars summary={data?.summary} />
          ) : (
            <TrendBars
              trend={data?.trend}
              trendBucket={trendBucket}
              bucketDates={bucketDates}
              partialDates={partialDates}
            />
          )}
        </ChartRenderBoundary>
      )}
    </ChartCard>
  );
};
